import React, { useEffect, useState } from 'react';
import { SERVER_URL } from 'Constants';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import useStore from 'Hooks/useStore';
import { logErrorOnServer } from 'Services/Api/_HumanWritten/ClientsideErrorLoggingService';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import If from '../If/If';
import { getContactUsEmailAddress } from 'Services/Api/_HumanWritten/EmailTemplateEntityService';
import { themeStore } from 'Models/ThemeStore';
import Card from 'Views/Components/_HumanWritten/Card/Card';
import NavigationWrapper from 'Views/Components/Navigation/NavigationWrapper';

export interface ErrorProps {
	error: Error | null;
	errorInfo?: React.ErrorInfo | null;
}

export function SomethingWentWrong({ error, errorInfo }: ErrorProps) {
	const store = useStore();
	const [contactUsEmailAddress, setContactUsEmailAddress] = useState('');

	useEffect(() => {
		if (isNotNullOrUndefined(error)) {
			if (errorInfo === null) {
				return;
			}
			logErrorOnServer({
				loggedInUserId: store.userId,
				currentLocation: document.location.href.toString(),
				previousLocation: document.referrer,
				message: error.message,
				stack: error.stack,
				componentStack: errorInfo?.componentStack ?? undefined,
			}).then(() => {
				// Do nothing (the error will be logged in the server)
			});
		}
	}, [error, errorInfo]);

	useEffect(() => {
		getContactUsEmailAddress().then(x => {
			setContactUsEmailAddress(x);
		});
	}, []);

	return (
		<>
			<NavigationWrapper />
			<div className="body-content error-page">
				<div
					className="error-page__background"
					style={{
						backgroundImage: `url(${themeStore.errorPageBackgroundImageURL})`,
						backgroundSize: 'cover',
						backgroundPosition: 'center',
					}}
				/>
				<div className="error-page__content">
					<Card>
						<h5>Whoops</h5>
						<p className="body--content">Something went wrong on our end</p>
						<p className="body--content">
							We have documented this matter with our IT department and are actively working to resolve it
							in a timely manner.
						</p>
						<p className="body--content">
							If this error persists,
							<If condition={!!(window as any).Intercom}>
								{' '}
								<span
									style={{
										color: 'var(--colour-brand-primary)',
										textDecoration: 'underline',
										cursor: 'pointer',
									}}
									onKeyDown={() => {
										(window as any).Intercom('show');
									}}
									role="button"
									tabIndex={0}
									onClick={() => {
										(window as any).Intercom('show');
									}}
								>
									message us through live chat
								</span>
								.
							</If>
							<If condition={isNullOrUndefined((window as any).Intercom)}>
								{' '}
								email us at{' '}
								<a
									href={`mailto:${contactUsEmailAddress}`}
									style={{ color: 'var(--colour-brand-primary)' }}
								>
									{contactUsEmailAddress}
								</a>{' '}
								and we will assist you further.
							</If>
						</p>
						<p className="body--content">We apologise for the inconvenience.</p>
						<Button
							display={Display.Solid}
							colors={Colors.Primary}
							newStyles
							onClick={() => {
								document.location.assign(`${SERVER_URL}/home`);
							}}
						>
							Go to home page
						</Button>
					</Card>
				</div>
			</div>
		</>
	);
}
